import React, { useEffect, useState } from 'react';
import { MapPinIcon, PlusIcon, TrashIcon, TruckIcon } from '@heroicons/react/24/outline';
import { travelAPI } from '../../services/api';
import type { SaveEventTravelRequest, Vehicle } from '../../types/travel';

/** Egy megálló az új esemény utazásában: név és indulási idő (az esemény napján). */
export interface DraftStop {
  key: string;
  name: string;
  time: string;
}

/** Egy jármű: a klub járműve (vehicleId) vagy kézzel megadott, a megállóival. */
export interface DraftVehicle {
  key: string;
  vehicleId: number | null;
  name: string;
  seats: number;
  driverName: string;
  stops: DraftStop[];
}

interface NewEventTravelFieldsProps {
  vehicles: DraftVehicle[];
  onChange: (vehicles: DraftVehicle[]) => void;
  /** Az esemény kezdése (HH:mm) - a megálló alapideje ehhez képest egy órával korábban. */
  startTime: string;
}

// Mobilon 16px, különben az iOS ránagyít a mezőre
const inputClass =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

let keySeq = 0;
const newKey = (prefix: string) => `${prefix}${Date.now().toString(36)}${(keySeq++).toString(36)}`;

/** Egy órával a kezdés előtt (HH:mm), az első megálló javasolt ideje. */
const hourBefore = (startTime: string) => {
  const [h, m] = startTime.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return '';
  const total = Math.max(0, h * 60 + m - 60);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

/**
 * Utazás az új eseménnyel együtt: milyen járművek mennek, és honnan, mikor
 * indulnak. Az utasokat nem itt osztjuk be - a családok a visszajelzésnél
 * jelzik, hogyan jutnak el, a felajánlott autók pedig maguktól bekerülnek.
 */
const NewEventTravelFields: React.FC<NewEventTravelFieldsProps> = ({ vehicles, onChange, startTime }) => {
  const [fleet, setFleet] = useState<Vehicle[]>([]);

  useEffect(() => {
    travelAPI
      .getVehicles()
      .then(setFleet)
      .catch(() => setFleet([]));
  }, []);

  const update = (key: string, patch: Partial<DraftVehicle>) =>
    onChange(vehicles.map((v) => (v.key === key ? { ...v, ...patch } : v)));

  const addVehicle = (fromFleet?: Vehicle) =>
    onChange([
      ...vehicles,
      {
        key: newKey('v'),
        vehicleId: fromFleet?.id ?? null,
        name: fromFleet?.name ?? '',
        seats: fromFleet?.seats ?? 8,
        driverName: '',
        stops: [{ key: newKey('s'), name: '', time: hourBefore(startTime) }],
      },
    ]);

  const updateStop = (vehicle: DraftVehicle, stopKey: string, patch: Partial<DraftStop>) =>
    update(vehicle.key, { stops: vehicle.stops.map((s) => (s.key === stopKey ? { ...s, ...patch } : s)) });

  const unusedFleet = fleet.filter((f) => !vehicles.some((v) => v.vehicleId === f.id));

  return (
    <div className="space-y-3">
      {vehicles.map((vehicle, index) => (
        <div key={vehicle.key} className="rounded-lg border border-gray-200 bg-white p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <TruckIcon className="h-5 w-5 text-gray-500" />
              {index + 1}. jármű
            </p>
            <button
              type="button"
              onClick={() => onChange(vehicles.filter((v) => v.key !== vehicle.key))}
              aria-label="Jármű törlése"
              className="flex h-9 w-9 items-center justify-center rounded-md text-gray-500 hover:bg-red-50 hover:text-red-600"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-[1fr_5.5rem] gap-2 sm:grid-cols-[1fr_6rem_1fr]">
            <input
              value={vehicle.name}
              onChange={(e) => update(vehicle.key, { name: e.target.value })}
              maxLength={100}
              required
              placeholder="Jármű neve (pl. Kisbusz)"
              aria-label="Jármű neve"
              className={inputClass}
            />
            <input
              type="number"
              min={1}
              max={60}
              value={vehicle.seats || ''}
              onChange={(e) => update(vehicle.key, { seats: Number(e.target.value) })}
              required
              aria-label="Férőhely"
              title="Utasférőhely a sofőrön kívül"
              placeholder="Hely"
              className={inputClass}
            />
            <input
              value={vehicle.driverName}
              onChange={(e) => update(vehicle.key, { driverName: e.target.value })}
              maxLength={100}
              placeholder="Sofőr (nem kötelező)"
              aria-label="Sofőr"
              className={`${inputClass} col-span-2 sm:col-span-1`}
            />
          </div>

          <div className="mt-3 space-y-2">
            <p className="text-xs font-medium text-gray-600">Megállók - honnan, mikor indul</p>
            {vehicle.stops.map((stop) => (
              <div key={stop.key} className="grid grid-cols-[minmax(0,1fr)_6.5rem_2.25rem] items-center gap-2">
                <div className="relative min-w-0">
                  <MapPinIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    value={stop.name}
                    onChange={(e) => updateStop(vehicle, stop.key, { name: e.target.value })}
                    maxLength={200}
                    required
                    placeholder="Pl. Klubház"
                    aria-label="Megálló neve"
                    className={`${inputClass} min-w-0 pl-8`}
                  />
                </div>
                <input
                  type="time"
                  value={stop.time}
                  onChange={(e) => updateStop(vehicle, stop.key, { time: e.target.value })}
                  aria-label="Indulás ideje"
                  className={`${inputClass} min-w-0 px-2`}
                />
                <button
                  type="button"
                  onClick={() => update(vehicle.key, { stops: vehicle.stops.filter((s) => s.key !== stop.key) })}
                  aria-label="Megálló törlése"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-gray-400 hover:bg-red-50 hover:text-red-600"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                update(vehicle.key, {
                  stops: [
                    ...vehicle.stops,
                    { key: newKey('s'), name: '', time: vehicle.stops[vehicle.stops.length - 1]?.time ?? hourBefore(startTime) },
                  ],
                })
              }
              className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-50"
            >
              <PlusIcon className="h-4 w-4" />
              Megálló
            </button>
          </div>
        </div>
      ))}

      <div className="flex flex-wrap gap-2">
        {unusedFleet.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => addVehicle(f)}
            className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-sm font-medium text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50"
          >
            <PlusIcon className="h-4 w-4" />
            {f.name} <span className="text-gray-400">({f.seats} hely)</span>
          </button>
        ))}
        <button
          type="button"
          onClick={() => addVehicle()}
          className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-sm font-medium text-primary-700 ring-1 ring-primary-200 hover:bg-primary-50"
        >
          <PlusIcon className="h-4 w-4" />
          Másik jármű
        </button>
      </div>

      <p className="text-xs text-gray-500">
        A családok a visszajelzésnél jelzik, hogyan jutnak el, és ha autóval mennek, felajánlhatják - ezek az autók maguktól
        bekerülnek az utazásba. Az utasokat később az esemény „Utazás szerkesztése” gombjával osztod be.
      </p>
    </div>
  );
};

/** A szerkesztett járművekből a mentési kérés (az esemény kezdőnapjával). */
export const buildTravelRequest = (vehicles: DraftVehicle[], startDate: string): SaveEventTravelRequest => ({
  vehicles: vehicles.map((v) => ({
    key: v.key,
    vehicleId: v.vehicleId,
    name: v.name.trim(),
    seats: v.seats,
    driverName: v.driverName.trim() || null,
    stops: v.stops
      .filter((s) => s.name.trim())
      .map((s) => ({
        key: s.key,
        name: s.name.trim(),
        meetTime: s.time ? `${startDate}T${s.time}:00` : null,
      })),
  })),
  passengers: [],
});

export default NewEventTravelFields;
